import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
  code?: string; // 适配用户的接口 code
  msg?: string; // 适配用户的接口 msg
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      console.log('errorThrower called with:', res);
      const { success, data, errorCode, errorMessage, showType, code, msg } =
        res as unknown as ResponseStructure;

      // 适配逻辑：优先判断 code === '0'，如果 code 存在
      if (code !== undefined) {
        const codeStr = String(code);
        // 兼容 0, "0", 200, "200" 为成功
        if (codeStr !== '0' && codeStr !== '200') {
          console.log('errorThrower throwing BizError (code check)');
          const error: any = new Error(msg || errorMessage || '请求失败');
          error.name = 'BizError';
          error.info = {
            errorCode: code,
            errorMessage: msg || errorMessage,
            showType,
            data,
          };
          throw error;
        }
      } else {
        // 默认逻辑：如果没有 code，则判断 success
        if (!success) {
          console.log('errorThrower throwing BizError (success check)');
          const error: any = new Error(errorMessage);
          error.name = 'BizError';
          error.info = { errorCode, errorMessage, showType, data };
          throw error; // 抛出自制的错误
        }
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      console.log('errorHandler called with:', error);
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        const { data } = error.response;
        if (data && (data.msg || data.errorMessage)) {
          message.error(data.msg || data.errorMessage);
        } else {
          message.error(`Response status:${error.response.status}`);
        }
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        console.error('Request made but no response received:', error.request);
        console.error('Full Error Object:', error);

        // 尝试解析可能的响应（即使 axios 认为它失败了）
        if (error.request.response) {
          console.log('Raw response in error.request:', error.request.response);
        }

        message.error(`None response! ${error.message}`);
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 拦截请求配置，进行个性化处理。
      const url = config?.url?.concat('?token=123');
      return { ...config, url };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      const { data } = response as unknown as ResponseStructure;

      if (data?.success === false) {
        message.error('请求失败！');
      }
      return response;
    },
  ],
};
